import { type ChangeEvent, type FC } from "react";
import { Button, Checkbox, Divider, Toolbar, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import type { MutationErrorLike } from "@/partner/shared/lib";
import { ToolbarProgress, useConfirmAction } from "@/shared/components";
import {
  marketingErrorKey,
  useArchiveAutomationsMutation,
  useDeleteAutomationsMutation,
} from "@/partner/entities/marketing";
import {
  automationDeleteConfirm,
  type AutomationBulkSelection,
} from "../model/automationBulkSelection";

interface AutomationsListToolbarProps {
  indeterminate: boolean;
  allSelected: boolean;
  numSelected: number;
  onSelectAllClick: (event: ChangeEvent<HTMLInputElement>) => void;
  /** Чистый результат выбора (model/automationBulkSelection.ts, тест там же). */
  selection: AutomationBulkSelection;
  onCloseSelected: () => void;
}

/**
 * Тулбар выбранных автоматизаций. В архив можно любую: архив и есть способ
 * остановить автоматизацию (неотправленные письма не уйдут, об этом текст
 * подтверждения). Удалить можно только черновик и архив; выбранные активные
 * и приостановленные пропускаются на клиенте (automationDeleteConfirm), а
 * не валят массовое удаление целиком отказом бэкенда
 * (AUTOMATION_NOT_DELETABLE). Кнопки дизейблятся на время мутации
 * (`disabled={loading}`), поэтому второй клик до ответа сервера не
 * запускает мутацию повторно: см. AutomationsListToolbar.test.tsx.
 */
export const AutomationsListToolbar: FC<AutomationsListToolbarProps> = ({
  indeterminate,
  allSelected,
  numSelected,
  onSelectAllClick,
  selection,
  onCloseSelected,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { confirm, dialog: confirmDialog } = useConfirmAction();
  const onError = (error: MutationErrorLike) =>
    enqueueSnackbar(t(marketingErrorKey(error)), { variant: "error" });
  const [archiveAutomations, { loading: archiving }] =
    useArchiveAutomationsMutation();
  const [deleteAutomations, { loading: deleting }] =
    useDeleteAutomationsMutation();
  const loading = archiving || deleting;
  const deleteConfirm = automationDeleteConfirm(selection);

  const onArchiveSelected = () =>
    confirm({
      title: t("automations.bulk.archive"),
      text: t("automations.bulk.confirmArchive"),
      onConfirm: () =>
        archiveAutomations({
          variables: { ids: selection.selectedIds },
          onCompleted: () => {
            onCloseSelected();
            enqueueSnackbar(t("automations.bulk.archived"), {
              variant: "success",
            });
          },
          onError,
        }),
    });

  const onDeleteSelected = () =>
    confirm({
      title: t("automations.bulk.delete"),
      text: (
        <>
          {t("automations.bulk.confirmDelete")}
          {deleteConfirm.showSkipWarning && (
            <>
              <br />
              {t("automations.bulk.deleteSkipsActive")}
            </>
          )}
        </>
      ),
      onConfirm: () =>
        deleteAutomations({
          variables: { ids: deleteConfirm.ids },
          onCompleted: () => {
            onCloseSelected();
            enqueueSnackbar(t("automations.bulk.deleted"), {
              variant: "success",
            });
          },
          onError,
        }),
    });

  return (
    <Toolbar sx={{ position: "relative" }}>
      <Checkbox
        color="default"
        indeterminate={indeterminate}
        checked={allSelected}
        onChange={onSelectAllClick}
        slotProps={{
          input: { "aria-label": t("automations.list.selectAll") },
        }}
        sx={{ marginLeft: "-16px", marginRight: { xs: 0, sm: "16px" } }}
      />
      <Typography>
        {t("common.toolbar.selectedCount", { count: numSelected })}
      </Typography>
      <Divider
        orientation="vertical"
        variant="middle"
        sx={{ mx: 1.25, height: "2em" }}
      />
      <Button color="inherit" onClick={onArchiveSelected} disabled={loading}>
        {t("automations.bulk.archive")}
      </Button>
      <Button
        color="inherit"
        onClick={onDeleteSelected}
        disabled={loading || !deleteConfirm.canDelete}
      >
        {t("automations.bulk.delete")}
      </Button>
      <ToolbarProgress loading={loading} />
      {confirmDialog}
    </Toolbar>
  );
};
